// Side-effect import to ensure TradingView charting library is loaded
import '../../charting_library/charting_library.js';

import type { BaseComponentProps } from '@json-render/react';
import isObject from 'lodash/isObject';
import isString from 'lodash/isString';
import toLower from 'lodash/toLower';
import toUpper from 'lodash/toUpper';
import trim from 'lodash/trim';
import React, { useEffect, useRef } from 'react';
import type { z } from 'zod';

import type {
  ChartingLibraryWidgetOptions,
  EntityId,
  IChartingLibraryWidget,
  IChartWidgetApi,
  ResolutionString,
  TimeframeOption,
} from '../../charting_library';
import { Datafeed } from '../../datafeed';
import { getOverlayColor, MATERIAL_BLUE } from '../../utils/chart-colors';
import type { TradingViewChartProps as TradingViewChartSchema } from './catalog';

type TradingViewChartProps = z.infer<typeof TradingViewChartSchema>;

// SeriesType enum values from charting_library.d.ts — Line=2, Candles=1.
// We intentionally expose only these two chart styles through the GenUI schema.
const CHART_TYPE_MAP: Record<string, number> = {
  line: 2,
  candles: 1,
};

// Hard cap on how long we'll wait for an individual study to finish loading
// before the headless renderer gives up and proceeds. Pure-computation studies
// (Volume, MA, RSI) don't go through the datafeed counter in helpers.ts, so we
// instrument createStudy directly. 30s matches the upper bound of a normal
// chart's data fetch + study compute window — anything past that is broken.
const STUDY_READY_TIMEOUT_MS = 30000;

declare global {
  interface Window {
    // In-flight study counter consumed by PrintArtifactPage's readiness signal.
    // Mirrors __pendingDatafeedRequests but for studies whose data work happens
    // post-datafeed (e.g. derived overlays, indicators).
    __pendingStudies?: number;
  }
}

const incrementPendingStudies = (): void => {
  if (typeof window === 'undefined') return;
  window.__pendingStudies = (window.__pendingStudies ?? 0) + 1;
};
const decrementPendingStudies = (): void => {
  if (typeof window === 'undefined') return;
  window.__pendingStudies = Math.max(0, (window.__pendingStudies ?? 0) - 1);
};

/**
 * Wraps a `chart.createStudy(...)` call so the print page knows when the study
 * is fully loaded. Increments `window.__pendingStudies` synchronously, then
 * decrements once either:
 *   - `IStudyApi.onDataLoaded()` fires (success)
 *   - `IStudyApi.onStudyError()` fires (study failed but we're done waiting)
 *   - 30s has elapsed (give up, log, continue — don't block the screenshot)
 *
 * The increment must happen on the same tick as the caller so the readiness
 * signal sees a non-zero counter before the next poll.
 */
const trackStudyReadiness = async (
  chart: IChartWidgetApi,
  promise: Promise<EntityId | null>,
): Promise<EntityId | null> => {
  incrementPendingStudies();
  let settled = false;
  const settle = (): void => {
    if (settled) return;
    settled = true;
    decrementPendingStudies();
  };
  const timeoutHandle = window.setTimeout(() => {
    console.warn(`Study readiness timeout (${STUDY_READY_TIMEOUT_MS}ms) — continuing without it`);
    settle();
  }, STUDY_READY_TIMEOUT_MS);
  try {
    const studyId = await promise;
    if (!studyId) {
      window.clearTimeout(timeoutHandle);
      settle();
      return null;
    }
    const study = chart.getStudyById(studyId);
    if (!study) {
      window.clearTimeout(timeoutHandle);
      settle();
      return studyId;
    }
    const done = (): void => {
      window.clearTimeout(timeoutHandle);
      settle();
    };
    // ISubscription.subscribe(scope, callback, singleshot) — singleshot=true so
    // the listener auto-unsubscribes after the first invocation.
    study.onDataLoaded().subscribe(null, done, true);
    study.onStudyError().subscribe(null, done, true);
    return studyId;
  } catch (error) {
    window.clearTimeout(timeoutHandle);
    settle();
    throw error;
  }
};

/**
 * Parse the GenUI `dateRange` prop into the TradingView `timeframe` widget option.
 * Accepts either a relative string ("3M", "1Y", ...) or an object with
 * YYYY-MM-DD strings, which we convert to UNIX seconds. Invalid inputs are
 * dropped (returns undefined) so the chart falls back to TradingView's default.
 */
const parseDateRange = (raw: unknown): TimeframeOption | undefined => {
  if (!raw) return undefined;
  if (isString(raw)) {
    // TradingView's timeframe grammar only has <n>D / <n>M / YTD / ALL — a
    // year unit ("1Y") is invalid and poisons the widget's initial range
    // math ('Key is NaN' at the first datafeed onResult, blank pane)
    const t = toUpper(trim(raw));
    if (t === 'YTD' || t === 'ALL') return t as TimeframeOption;
    const m = t.match(/^(\d+)([DWMY])$/);
    if (!m) return undefined;
    const n = parseInt(m[1], 10);
    if (m[2] === 'D') return `${n}D` as TimeframeOption;
    if (m[2] === 'W') return `${n * 7}D` as TimeframeOption;
    if (m[2] === 'M') return `${n}M` as TimeframeOption;
    return `${n * 12}M` as TimeframeOption;
  }
  if (isObject(raw)) {
    const obj = raw as { from?: unknown; to?: unknown };
    const fromMs = isString(obj.from) ? new Date(obj.from).getTime() : NaN;
    const toMs = isString(obj.to) ? new Date(obj.to).getTime() : NaN;
    if (!Number.isFinite(fromMs) || !Number.isFinite(toMs)) return undefined;
    // TradingView expects UNIX seconds, not milliseconds (d.ts:4768).
    return { from: Math.floor(fromMs / 1000), to: Math.floor(toMs / 1000) };
  }
  return undefined;
};

/**
 * TradingViewChart — generative UI component that renders an interactive
 * TradingView Advanced Chart for a given symbol (e.g. "CME:CL.M1!").
 */
export const TradingViewChart = ({ props }: BaseComponentProps<TradingViewChartProps>) => {
  const symbol = props.symbol;
  const interval = props.interval ?? 'D';
  const height = props.height ?? 500;
  const title = props.title;
  const showIntraday = props.showIntraday ?? false;
  const chartTypeProp = props.chartType;
  const dateRangeProp = props.dateRange;
  const studiesProp = props.studies;
  const comparedTradesProp = props.comparedTrades;

  const chartContainerRef = useRef<HTMLDivElement>(null);
  const widgetRef = useRef<IChartingLibraryWidget | null>(null);
  // Track created Overlay study IDs so we can remove them on comparedTrades
  // changes — mirrors the pattern in components/tradingview-advanced-chart.tsx.
  // EntityId is a branded string type (Nominal<string, "EntityId">) from the
  // charting library, required by chart.removeEntity().
  const overlayStudyIds = useRef<EntityId[]>([]);
  // Set to true inside onChartReady so the overlay effect knows when the chart
  // API is safe to call; reset in cleanup.
  const chartReadyRef = useRef<boolean>(false);

  // Stringify object/array props so the effect re-runs on content change, not
  // on fresh reference identity (GenUI re-renders produce new objects each time).
  const dateRangeKey = JSON.stringify(dateRangeProp ?? null);
  const studiesKey = JSON.stringify(studiesProp ?? null);
  const comparedTradesKey = JSON.stringify(comparedTradesProp ?? null);

  useEffect(() => {
    if (!chartContainerRef.current || !symbol) return;

    const datafeedInstance = new Datafeed({ showIntraday });
    const initialChartType = CHART_TYPE_MAP[toLower(chartTypeProp ?? 'line')] ?? CHART_TYPE_MAP.line;
    const timeframe = parseDateRange(dateRangeProp);

    const widgetOptions: ChartingLibraryWidgetOptions = {
      symbol,
      datafeed: datafeedInstance,
      interval: interval as ResolutionString,
      container: chartContainerRef.current,
      library_path: '/charting_library/',
      custom_css_url: 'custom.css',
      locale: 'en',
      disabled_features: [
        'header_compare',
        'header_symbol_search',
        'header_quick_search',
        'symbol_search_hot_key',
        'legend_inplace_edit',
        'display_market_status',
        'header_saveload',
      ],
      enabled_features: [
        'header_in_fullscreen_mode',
        'side_toolbar_in_fullscreen_mode',
        'inactivity_gaps',
      ],
      fullscreen: false,
      autosize: true,
      overrides: {
        // Newer charting_library versions default line charts to a
        // magenta->cyan gradient stroke (lineStyle.colorType 'gradient'),
        // which reads as two different series; pin the classic solid blue
        'mainSeriesProperties.lineStyle.colorType': 'solid',
        'mainSeriesProperties.lineStyle.color': MATERIAL_BLUE,
        'paneProperties.backgroundType': 'solid',
        'paneProperties.background': '#ffffff',
        'paneProperties.backgroundGradientStartColor': '#ffffff',
        'paneProperties.backgroundGradientEndColor': '#ffffff',
        'paneProperties.vertGridProperties.color': '#e1e4e8',
        'paneProperties.horzGridProperties.color': '#e1e4e8',
        'backgrounds.preMarket.color': '#ffffff',
        'backgrounds.postMarket.color': '#ffffff',
      },
      ...(showIntraday
        ? {
          time_frames: [
            { text: '1D', resolution: '1' as ResolutionString, description: '1 Day' },
            { text: '5D', resolution: '5' as ResolutionString, description: '5 Days' },
            { text: '1M', resolution: '30' as ResolutionString, description: '1 Month' },
            { text: '3M', resolution: '60' as ResolutionString, description: '3 Months' },
            { text: '6M', resolution: '120' as ResolutionString, description: '6 Months' },
            { text: '1Y', resolution: '1W' as ResolutionString, description: '1 Year' },
            { text: '5Y', resolution: '1W' as ResolutionString, description: '5 Years' },
            { text: '100Y', resolution: '1M' as ResolutionString, description: 'All', title: 'All' },
          ],
        }
        : {
          time_frames: [
            { text: '1M', resolution: '1D' as ResolutionString, description: '1 Month' },
            { text: '3M', resolution: '1D' as ResolutionString, description: '3 Months' },
            { text: '6M', resolution: '1D' as ResolutionString, description: '6 Months' },
            { text: '1Y', resolution: '1D' as ResolutionString, description: '1 Year' },
            { text: '5Y', resolution: '1W' as ResolutionString, description: '5 Years' },
            { text: '100Y', resolution: '1M' as ResolutionString, description: 'All', title: 'All' },
          ],
        }),
      // Keep both Line and Candles one click away in the toolbar regardless of
      // the initial chartType selected via props.
      favorites: { chartTypes: ['Line', 'Candles'] },
      ...(timeframe ? { timeframe } : {}),
    };

    // TradingView widget constructor is loaded via the side-effect import above
    // eslint-disable-next-line new-cap
    const tvWidget = new (window as unknown as { TradingView: { widget: new (opts: ChartingLibraryWidgetOptions) => IChartingLibraryWidget } }).TradingView.widget(widgetOptions);
    widgetRef.current = tvWidget;

    tvWidget.onChartReady(() => {
      const chart = tvWidget.chart();
      // Apply prop-selected chart type (defaults to Line)
      chart.setChartType(initialChartType);
      // Remove empty right-side offset so data fills the chart
      chart.getTimeScale().defaultRightOffset().setValue(0);
      // Enable inactivity gaps display
      chart.inactivityGaps().setValue(true);

      // Pre-load any studies requested via props. Fire-and-forget per study —
      // failures should not block the rest of the chart from rendering.
      // Pattern mirrors tradingview-advanced-chart.tsx:243–265.
      // The Zod schema types inputs/overrides permissively (Record<string, unknown>)
      // so the LLM can supply arbitrary keys. `createStudy`'s generic expects
      // StudyInputValue and Partial<SingleIndicatorOverrides>; TradingView
      // validates at runtime, so we pass through via an `any` cast of the method.
      for (const study of studiesProp ?? []) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const studyPromise = (chart.createStudy as any)(
          study.name,
          study.forceOverlay ?? false,
          false,
          study.inputs,
          study.overrides,
        ) as Promise<EntityId | null> | undefined;
        if (studyPromise) {
          trackStudyReadiness(chart, studyPromise).catch((error: unknown) => {
            console.error(`Failed to load study "${study.name}":`, error);
          });
        }
      }

      // Mark the chart as ready so the comparedTrades effect can safely call
      // createStudy/removeEntity. Must be set AFTER the pre-loaded studies loop
      // so overlays aren't raced against the initial chart setup.
      chartReadyRef.current = true;
    });

    return () => {
      chartReadyRef.current = false;
      overlayStudyIds.current = [];
      if (widgetRef.current) {
        widgetRef.current.remove();
        widgetRef.current = null;
      }
    };
    // studiesProp/dateRangeProp are tracked via their stringified keys below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, interval, showIntraday, chartTypeProp, dateRangeKey, studiesKey]);

  // Manage Overlay studies for comparedTrades. Mirrors the pattern from
  // components/tradingview-advanced-chart.tsx:210–269 but without redux —
  // GenUI components stay framework-light.
  useEffect(() => {
    const widget = widgetRef.current;
    if (!widget) return;

    const comparedTrades = comparedTradesProp ?? [];

    const updateOverlays = async () => {
      // Re-read the widget inside the async closure in case the outer effect
      // re-ran and swapped it out between the onChartReady callback and now.
      const currentWidget = widgetRef.current;
      if (!currentWidget) return;
      const chart = currentWidget.chart();

      // Clear any previously-added overlay studies before re-adding. Ignore
      // errors from stale ids — mirrors legacy behavior at lines 227–232.
      for (const studyId of overlayStudyIds.current) {
        try {
          chart.removeEntity(studyId);
        } catch (e) {
          console.debug('Failed to remove overlay entity:', e);
        }
      }
      overlayStudyIds.current = [];

      for (let i = 0; i < comparedTrades.length; i++) {
        const trade = comparedTrades[i];
        try {
          const color = trade.color || getOverlayColor(i);
          // createStudy's typed signature is narrower than what TradingView
          // accepts at runtime — cast through any to pass the overlay symbol
          // and per-leg color overrides. `plot.*` is used by Compare, `lineStyle.*`
          // by Overlay; set both so we're covered across TradingView versions.
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          const studyPromise = (chart.createStudy as any)(
            'Overlay',
            true, // forceOverlay — place on main pane
            false, // lock
            { symbol: trade.formula },
            {
              'plot.color': color,
              'lineStyle.color': color,
              'plot.linewidth': 2,
              'lineStyle.linewidth': 2,
            },
            { priceScale: 'as-series' },
          ) as Promise<EntityId | null>;
          const studyId = await trackStudyReadiness(chart, studyPromise);
          if (studyId) {
            overlayStudyIds.current.push(studyId);
          }
        } catch (error) {
          console.error(`Failed to add overlay for ${trade.formula}:`, error);
        }
      }
    };

    if (chartReadyRef.current) {
      updateOverlays();
    } else {
      // Chart not ready yet — defer until onChartReady marks the flag. We
      // subscribe via the widget's onChartReady rather than re-triggering our
      // own effect, so overlays land exactly once per change.
      widget.onChartReady(() => {
        updateOverlays();
      });
    }
    // comparedTradesProp is tracked via its stringified key below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, comparedTradesKey]);

  if (!symbol) {
    return (
      <div className="flex items-center justify-center h-48 text-muted-foreground text-sm">
        No symbol provided
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-1">
      {title && <div className="text-sm font-semibold text-foreground mb-1">{title}</div>}
      <div ref={chartContainerRef} style={{ height }} />
    </div>
  );
};
